import { Tooltip } from "@mui/material";
import { checkFileType, fileExtensions, type AttachmentItem } from "@/lib/methodUtils";
import { memo, useMemo } from "react";
import { useState } from "react";
import PreviewDialog from "../PreviewDialog/PreviewDialog";
import PdfPreviewDialog from "../PreviewDialog/PdfPreviewDialog";

interface AttachmentPreviewProps {
  attachments: AttachmentItem[];
  showAttachmentLabel?: boolean;
  width?: number;
  fieldName?: string;
}

const AttachmentPreview = ({
  attachments,
  showAttachmentLabel = true,
  width = 28,
  fieldName = "",
}: AttachmentPreviewProps) => {
  const [preview, setPreview] = useState({
    open: false,
    urls: [] as string[],
    names: [] as string[],
    types: [] as ("image" | "video")[],
    index: 0,
    mode: "" as "media" | "pdf" | "",
  });

  const { imageFiles, otherFiles } = useMemo(() => {
    const images: AttachmentItem[] = [];
    const others: AttachmentItem[] = [];

    attachments.forEach((file) => {
      const ext = file.extension?.toLowerCase() ?? "";
      if (fileExtensions.image.has(ext)) {
        images.push(file);
      } else {
        others.push(file);
      }
    });

    return {
      imageFiles: images,
      otherFiles: others
    };
  }, [attachments]);

  return (
    <div className="text-gray-500">
      {showAttachmentLabel && (
        <label className="text-xl font-medium">
          Attachments ({attachments.length})
        </label>
      )}

      {fieldName && (
        <label className="text-sm font-medium">
          {fieldName}
        </label>
      )}

      {/* Files */}
      <div className="flex flex-wrap items-start gap-4 mt-2">
        {/* IMAGE GROUP */}
        {imageFiles.length > 0 && (
          <div className="flex flex-col items-center">
            <Tooltip
              title={
                imageFiles.length > 1
                  ? `${imageFiles.length} Images`
                  : imageFiles[0]?.name || "Image"
              }
            >
              <img
                src="/assets/media/fileIcon/image.svg"
                width={width}
                className="pb-2 cursor-pointer transition-transform hover:scale-105"
                onClick={() =>
                  setPreview({
                    open: true,
                    urls: imageFiles.map(f => f.url),
                    names: imageFiles.map(f => f.name),
                    types: imageFiles.map(() => "image"),
                    index: 0,
                    mode: "media",
                  })
                }
              />
            </Tooltip>

            {showAttachmentLabel && (
              <span className="text-sm font-medium text-center">
                {imageFiles.length > 1
                  ? `Images (${imageFiles.length})`
                  : "Image"}
              </span>
            )}
          </div>
        )}

        {/* Other Files */}
        {otherFiles.map((file) => {
          const ext = file.extension?.toLowerCase() ?? "";
          const isWord = fileExtensions.word.has(ext);
          const isVideo = fileExtensions.video.has(ext);
          const isPdf = fileExtensions.pdf.has(ext);
          const isExcel = fileExtensions.excel?.has(ext);

          const label =
            file.label !== "Attachment"
              ? file.label
              : isWord
                ? "Word"
                : isVideo
                  ? "Video"
                  : isPdf
                    ? "PDF"
                    : isExcel
                      ? "Excel"
                      : "File";

          const iconSrc = isWord
            ? "/assets/media/fileIcon/word.svg"
            : isVideo
              ? "/assets/media/fileIcon/video.svg"
              : isPdf
                ? "/assets/media/fileIcon/pdf.svg"
                : isExcel
                  ? "/assets/media/fileIcon/excel.svg"
                  : "/assets/media/fileIcon/file.svg";

          return (
            <span key={`${file.url}-${file.name}`}>
              <p className="flex flex-col items-center">
                <Tooltip title={file.name}>
                  <img
                    src={iconSrc}
                    width={width}
                    className="pb-2 cursor-pointer transition-transform hover:scale-105"
                    onClick={() => {
                      if (isVideo) {
                        setPreview({
                          open: true,
                          urls: [file.url],
                          names: [file.name],
                          types: ["video"],
                          index: 0,
                          mode: "media",
                        });
                      } else if (isPdf) {
                        setPreview({
                          open: true,
                          urls: [file.url],
                          names: [file.name],
                          types: [],
                          index: 0,
                          mode: "pdf",
                        });
                      } else {
                        checkFileType(file.url, file.name, file.extension);
                      }
                    }}
                  />
                </Tooltip>

                {showAttachmentLabel && (
                  <span className="text-sm font-medium text-center break-all max-w-[70px]">
                    {label}
                  </span>
                )}
              </p>
            </span>
          );
        })}
      </div>

      {/* Pdf Preview Modal */}
      <PdfPreviewDialog
        open={preview.open && preview.mode === "pdf"}
        fileUrl={preview.urls[0] || ""}
        fileName={preview.names[0] || ""}
        onClose={() =>
          setPreview((p) => ({
            ...p,
            open: false,
            mode: "",
          }))
        }
      />

      {/* Media Modal */}
      <PreviewDialog
        open={preview.open && preview.mode === "media"}
        fileUrl={preview.urls}
        fileName={preview.names}
        fileType={preview.types}
        initialIndex={preview.index}
        onClose={() =>
          setPreview((p) => ({
            ...p,
            open: false,
            mode: "",
          }))
        }
      />
    </div>
  );
};

export default memo(AttachmentPreview);
